import { createEvent, createStore } from 'effector';
import type { CurrentObject } from '@buildex/module-contract';

/**
 * Единый «текущий объект» кабинета: выбирается на Главной (список объектов)
 * или в шапке модуля, персистится в localStorage, читается всеми модулями.
 * До появления GET /objects у App API имя объекта может совпадать с id.
 */

const STORAGE_KEY = 'buildex.currentObject';

function readStored(): CurrentObject | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<CurrentObject>;
    return typeof parsed.id === 'string' && parsed.id.length > 0
      ? { id: parsed.id, name: typeof parsed.name === 'string' ? parsed.name : parsed.id }
      : null;
  } catch {
    return null;
  }
}

function writeStored(value: CurrentObject | null): void {
  if (typeof localStorage === 'undefined') return;
  if (value === null) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

/** Выбрать объект (id обязателен; name — если известно) */
export const objectSelected = createEvent<CurrentObject | string>();
/** Сбросить выбор (например, при смене тенанта) */
export const objectCleared = createEvent<void>();

function normalize(payload: CurrentObject | string): CurrentObject | null {
  const obj = typeof payload === 'string' ? { id: payload, name: payload } : payload;
  const id = obj.id.trim();
  if (id.length === 0) return null;
  const name = obj.name.trim().length > 0 ? obj.name.trim() : id;
  return { id, name };
}

export const $currentObject = createStore<CurrentObject | null>(readStored())
  .on(objectSelected, (current, payload) => {
    const next = normalize(payload);
    if (next === null) return current;
    // Имя уточнилось (пришло из списка объектов) — обновляем, id тот же
    if (current !== null && current.id === next.id && current.name === next.name) return current;
    return next;
  })
  .reset(objectCleared);

/** Только id — удобно как source для эффектов модулей */
export const $currentObjectId = $currentObject.map((o) => (o === null ? null : o.id));

// Персист — побочный эффект вне графа
$currentObject.updates.watch(writeStored);
